import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/store";
import { useEnvironmentStore } from "@/store/useEnvironmentStore";
import { Loader, TryAgain } from "@/components";
import { reportTelemetryFailure } from "@/core/telemetry";
import CONFIGS from "@/core/utils/configs";
import styles from "@/components/IframeShell/IframeShell.module.css";
import type { IframeShellProps } from "@/components/IframeShell/IframeShell.types";

const isNonProd = CONFIGS.ENVIRONMENT !== "prod";

const IframeShell: FC<IframeShellProps> = ({ theme, teamsClientType }) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [loginReady, setLoginReady] = useState(!isNonProd);
  const [retryCount, setRetryCount] = useState(0);
  const front = useStore((state) => state.config.front);
  const token = useStore((state) => state.config.token);
  const companyId = useStore((state) => state.config.companyId);
  const companyName = useStore((state) => state.config.companyName);
  const environmentFrontendUrl = useEnvironmentStore((state) =>
    state.getFrontendUrl(),
  );
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const handleLoad = () => {
    if (!loginReady) {
      setLoginReady(true);
      return;
    }
    setIsLoading(false);
  };

  const handleError = () => {
    if (!loginReady) {
      setLoginReady(true);
      return;
    }
    reportTelemetryFailure("frame_load_failed", { companyId, companyName });
    setHasError(true);
  };

  // React does not delegate the "error" event for <iframe>, so onError never fires; bind it manually.
  useEffect(() => {
    const iframeElement = iframeRef.current;
    if (!iframeElement) return;
    iframeElement.addEventListener("error", handleError);
    return () => iframeElement.removeEventListener("error", handleError);
  });

  const finalFrontendUrl = environmentFrontendUrl || front;

  if (!finalFrontendUrl) {
    return null;
  }

  const params = new URLSearchParams();
  const embedSource = teamsClientType ? `teams-${teamsClientType}` : "teams";
  params.set("embed_source", embedSource);
  if (theme) {
    params.set("theme", theme);
  }
  if (token) {
    params.set("jwttoken", token);
  }
  if (companyId) {
    params.set("companyId", String(companyId));
  }
  if (companyName) {
    // Passed in the URL because the frontend cannot rely on the riseup-subdomain
    // cookie inside the iframe when third-party cookies are blocked.
    const subdomainCookie = btoa(
      JSON.stringify({ name: companyName, custom: 0 }),
    );
    params.set("riseup-subdomain", subdomainCookie);
  }
  const iframeUrl = `${finalFrontendUrl}?${params.toString()}`;

  const loginIframeUrl =
    isNonProd && companyId && companyName
      ? `${finalFrontendUrl}/Debug/loginAsCompany/${companyId}`
      : null;

  const currentSrc = loginIframeUrl && !loginReady ? loginIframeUrl : iframeUrl;

  const handleRetry = () => {
    setHasError(false);
    setIsLoading(true);
    setLoginReady(!isNonProd);
    setRetryCount((count) => count + 1);
  };

  if (hasError) {
    return (
      <div className={styles.container}>
        <TryAgain
          title={t("Something went wrong")}
          description={t("Try to contact us")}
          buttonTitle={t("Try again")}
          onClick={handleRetry}
        />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {isLoading && loginReady && (
        <Loader label={t("Launching application...")} />
      )}
      <iframe
        ref={iframeRef}
        key={retryCount}
        src={currentSrc}
        className={`${styles.iframe} ${
          !loginReady || isLoading ? styles.iframeHidden : styles.iframeVisible
        }`}
        title="Rise Up"
        allow="camera; microphone; fullscreen; autoplay; clipboard-write"
        onLoad={handleLoad}
      />
    </div>
  );
};

export default IframeShell;
